import { defineStore } from 'pinia'
import axiosInstance from '@/config/axios'
import { usePortfolioStore } from './portfolioStore'
import axios from 'axios'
import { logger } from '@/utils/logger'
export interface AuthUser {
  id: number
  email?: string
  name?: string
  role_id?: number
}
interface AuthState {
  isAuthenticated: boolean
  user: AuthUser | null
  accessToken: string | null
  hasInitialized?: boolean
}
export const useAuthStore = defineStore('auth', {
  state: (): AuthState => {
    // Support both legacy and new storage keys
    const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken')
    return {
      isAuthenticated: !!token,
      user: null,
      accessToken: token,
      hasInitialized: false
    }
  },
  getters: {
    getUser: (state) => state.user,
    getIsAuthenticated: (state) => state.isAuthenticated,
    getAccessToken: (state) => state.accessToken,
    // Admin check unified for the app
    isAdmin: (state) => {
      try {
        return Boolean(state.user && state.user.role_id === 1)
      } catch {
        return false
      }
    }
  },
  actions: {
    async login(email: string, password: string) {
      const response = await axiosInstance.post('/auth/login/', { email, password })
      const data = response?.data
      if (!data || !data.token || !data.user) {
        throw new Error('Réponse invalide du serveur')
      }
      this.setAuth({ user: data.user, accessToken: data.token })
      // Initialiser le store de marché en temps réel après la connexion
      try {
        const { useRealtimeMarketStore } = await import('@/stores/realtimeMarketStore')
        const realtimeMarketStore = useRealtimeMarketStore()
        realtimeMarketStore.initialize()
        logger.debug('Store de marché en temps réel initialisé après login')
      } catch (error) {
        logger.error('Erreur lors de l\'initialisation du WebSocket de marché après login:', error)
      }
      // Persist refresh token if provided by backend
      if (data.refresh) {
        try { localStorage.setItem('refreshToken', data.refresh) } catch { /* ignored */ }
      }
      return true
    },
    setAuth(data: { user: AuthUser, accessToken: string }) {
      // Log de débogage pour vérifier les données reçues
      if (import.meta.env.DEV) {
        logger.debug('auth.setAuth: données reçues', {
          user: data.user,
          tokenPresent: !!data.accessToken,
          tokenLength: data.accessToken ? data.accessToken.length : 0
        })
      }
      this.user = data.user
      this.accessToken = data.accessToken
      this.isAuthenticated = true
      // Persist using both keys to stay compatible with existing code paths
      localStorage.setItem('access_token', data.accessToken)
      localStorage.setItem('accessToken', data.accessToken)
      // Persist user to localStorage for restore on reload
      try { localStorage.setItem('user', JSON.stringify(data.user)) } catch { /* ignored */ }
      // Les en-têtes d'authentification sont gérés par l'intercepteur Axios centralisé
      // Vérifier que le token a bien été stocké
      if (import.meta.env.DEV) {
        const storedToken = localStorage.getItem('accessToken')
        logger.debug('auth.setAuth: token stocké dans localStorage', {
          present: !!storedToken,
          length: storedToken ? storedToken.length : 0
        })
      }
    },
    clearAuth() {
      logger.info('auth.clearAuth: nettoyage de l\'état et du stockage local')
      // Récupérer l'ID utilisateur AVANT de vider l'état
      const userId = this.user?.id
      this.user = null
      this.accessToken = null
      this.isAuthenticated = false
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('access_token')
      // Nettoyer toutes les clés localStorage liées aux portefeuilles (pour tous les utilisateurs)
      // Cela garantit qu'après déconnexion/reconnexion, on repart sur le portefeuille par défaut
      if (userId) {
        localStorage.removeItem(`selectedPortfolioId_${userId}`)
      }
      // Nettoyer aussi l'ancienne clé sans ID utilisateur pour compatibilité
      localStorage.removeItem('selectedPortfolioId')
      // Nettoyer toutes les autres clés potentielles (pour être sûr)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('selectedPortfolioId_')) {
          localStorage.removeItem(key)
        }
      })
      // Les en-têtes d'authentification seront retirés automatiquement par l'intercepteur lors des prochains appels
    },
    async logout() {
      logger.debug('logout() appelé')
      logger.info('auth.logout: déclenché (client)')
      // Conserver les tokens avant clearAuth (synchrone, prioritaire)
      const token = this.accessToken || localStorage.getItem('access_token') || localStorage.getItem('accessToken')
      const refresh = localStorage.getItem('refreshToken')
      logger.debug('Tokens récupérés:', { token: !!token, refresh: !!refresh })
      logger.debug('État auth avant clearAuth:', {
        isAuthenticated: this.isAuthenticated,
        user: this.user
      })
      this.clearAuth()
      logger.debug('État auth après clearAuth:', {
        isAuthenticated: this.isAuthenticated,
        user: this.user
      })
      try {
        const { userPreferencesService } = await import('@/services/userPreferences')
        userPreferencesService.clearCache()
      } catch (error) {
        logger.error('Erreur lors du vidage du cache des préférences:', error)
      }
      // Purger les caches de données sensibles côté client APRÈS de vider l'auth
      logger.debug('Reset du store portfolio')
      try {
        const portfolioStore = usePortfolioStore()
        logger.debug('État portfolio avant reset:', { 
          portfolios: portfolioStore.portfolios?.length || 0,
          currentPortfolio: !!portfolioStore.currentPortfolio 
        })
        portfolioStore.resetStore()
        logger.debug('État portfolio après reset:', { 
          portfolios: portfolioStore.portfolios?.length || 0,
          currentPortfolio: !!portfolioStore.currentPortfolio 
        })
      } catch (error) {
        logger.error('Erreur lors du reset du store portfolio:', error)
      }
      // Déconnecter le WebSocket de marché en temps réel
      try {
        const { useRealtimeMarketStore } = await import('@/stores/realtimeMarketStore')
        const realtimeMarketStore = useRealtimeMarketStore()
        realtimeMarketStore.disconnect()
        logger.debug('WebSocket de marché en temps réel déconnecté')
      } catch (error) {
        logger.error('Erreur lors de la déconnexion du WebSocket de marché:', error)
      }
      // Appel best-effort (ne pas bloquer la navigation) avec le token explicite
      if (token) {
        logger.debug('Appel backend logout')
        axiosInstance
          .post('/auth/logout/', { refresh }, { headers: { Authorization: `Bearer ${token}` } })
          .then(() => {
            logger.debug('Appel backend logout réussi')
            if (import.meta.env.DEV) logger.debug('auth.logout: appel backend réussi')
          })
          .catch((error) => {
            logger.error('Appel backend logout échoué:', error)
            if (import.meta.env.DEV) logger.debug('auth.logout: appel backend en erreur (best-effort)', error)
          })
      } else {
        logger.debug('Pas de token, pas d\'appel backend')
      }
    },
    // Ajouter une méthode pour vérifier l'authentification
    checkAuth() {
      // Read from both keys for compatibility
      const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken')
      if (import.meta.env.DEV) {
        logger.debug('auth.checkAuth', {
          tokenInStore: !!this.accessToken,
          tokenInLocalStorage: !!token,
          isAuthenticated: this.isAuthenticated
        })
      }
      // Si le token est dans localStorage mais pas dans le store, le restaurer
      if (token && !this.accessToken) {
        this.accessToken = token
        this.isAuthenticated = true
        // Les en-têtes d'authentification sont gérés par l'intercepteur Axios centralisé
        if (import.meta.env.DEV) {
          logger.debug('auth.checkAuth: token restauré depuis localStorage')
        }
        return true
      }
      // If we already have a token in memory, ensure axios header is set
      // Les en-têtes d'authentification sont gérés par l'intercepteur Axios centralisé
      return !!token
    },
    // Initialisation unique, idempotente
    initializeAuth() {
      if (this.hasInitialized) {
        return
      }
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken')
        const userRaw = localStorage.getItem('user')
        if (token) {
          this.accessToken = token
          this.isAuthenticated = true
          // Les en-têtes d'authentification sont gérés par l'intercepteur Axios centralisé
        }
        if (userRaw) {
          try { this.user = JSON.parse(userRaw) } catch { this.user = null }
        }
        this.hasInitialized = true
      } catch (e) {
        if (import.meta.env.DEV) {
          logger.error('auth.initializeAuth: erreur', e)
        }
      }
    },
    async refreshToken() {
      try {
        // Appel à l'API pour rafraîchir le token
        const refresh = localStorage.getItem('refreshToken')
        if (!refresh) return false
        // Utiliser un axios "nu" pour éviter toute interception / récursion
        const baseURL = import.meta.env.VITE_API_URL || '/api'
        const response = await axios.post(`${baseURL}/auth/token/refresh/`, { refresh })
        if (response.data && response.data.access) {
          // Mise à jour du token dans le store et localStorage
          this.accessToken = response.data.access
          localStorage.setItem('accessToken', response.data.access)
          localStorage.setItem('access_token', response.data.access)
          // Les en-têtes d'authentification sont gérés par l'intercepteur Axios centralisé
          return true
        }
        return false
      } catch (error) {
        // Utilisation du logger pour le débogage
        logger.error('auth.refreshToken: erreur lors du rafraîchissement du token', error)
        // En cas d'échec, déconnecter l'utilisateur
        this.clearAuth()
        return false
      }
    },
    async fetchMe() {
      try {
        const res = await axiosInstance.get('/auth/me/')
        if (res?.data) {
          this.user = res.data
          try { localStorage.setItem('user', JSON.stringify(res.data)) } catch { /* ignored */ }
        }
        return true
      } catch {
        // Si erreur 401, clear
        this.clearAuth()
        return false
      }
    },
    async updateProfile(payload: { name?: string; email?: string }) {
      const res = await axiosInstance.put('/auth/me/', payload)
      const requiresReauth = !!res?.data?.requiresReauth
      if (requiresReauth || (payload.email && payload.email !== (this.user?.email))) {
        await this.logout()
        return { requiresReauth: true, message: res?.data?.message || 'Profil mis à jour' }
      }
      // Mise à jour locale du nom si pas de reauth
      if (payload.name && this.user) {
        this.user = { ...this.user, name: payload.name }
        try { localStorage.setItem('user', JSON.stringify(this.user)) } catch { /* ignored */ }
      } else {
        // Sinon recharger depuis l'API
        await this.fetchMe()
      }
      return { requiresReauth: false, message: res?.data?.message || 'Profil mis à jour' }
    },
    async changePassword(payload: { oldPassword: string; newPassword: string }) {
      await axiosInstance.post('/auth/change-password/', {
        old_password: payload.oldPassword,
        new_password: payload.newPassword
      })
      // Déconnexion après changement de mot de passe
      await this.logout()
      return true
    },
    async deleteAccount() {
      // Planifie la suppression côté serveur puis déconnecte l'utilisateur
      try {
        const res = await axiosInstance.post('/auth/me/delete-async/')
        if (import.meta.env.DEV) logger.debug('auth.deleteAccount: suppression planifiée (202)', res?.data?.task_id)
      } catch (e) {
        if (import.meta.env.DEV) logger.error('auth.deleteAccount: échec planification (backend)', e)
      } finally {
        // Déconnexion côté client (même si la planification a échoué)
        await this.logout()
      }
      return true
    }
  }
})
